import axios, { AxiosError, AxiosInstance } from 'axios'
import type {
  User,
  GoogleAuthUrlResponse,
  Workspace,
  WorkspaceCreate,
  Agent,
  AgentCreate,
  Workflow,
  WorkflowCreate,
  Execution,
  ExecutionCreate,
  ExecutionStats,
  MCPServer,
  MCPServerCreate,
  MCPToolCall,
  Collection,
  CollectionCreate,
  Document,
  SearchResult,
  RAGRequest,
  RAGResponse,
  PaginatedResponse,
  AgentTeam,
  AgentTeamCreate,
  AgentTeamUpdate,
  TeamMemberCreate,
  TeamExecution,
  AgentMessage,
  KnowledgeGraph,
  KnowledgeGraphCreate,
  KnowledgeGraphUpdate,
  GraphNode,
  GraphNodeCreate,
  GraphNodeUpdate,
  GraphRelationship,
  GraphRelationshipCreate,
  GraphSearchRequest,
  GraphSearchResponse,
  GraphPathRequest,
  GraphPathResponse,
  GraphVisualizationData,
  GraphStatistics,
  AgentGraphAssociation,
  TeamGraphAssociation,
  NodeType,
  RelationType,
  ToolInfo,
  ToolListResponse,
  ToolExecutionRequest,
  ToolExecutionResponse,
  AgentToolConfig,
  ToolStatsResponse,
  Crew,
  CrewInvokeRequest,
  CrewInvokeResponse,
  AgentGraphResponse,
  LangGraphState,
} from '@/types'

// Determine API URL - use env var if set, otherwise derive from current location
const getApiBaseUrl = () => {
  const envUrl = import.meta.env.VITE_API_URL
  // Empty string = same-origin (production with nginx proxy)
  if (envUrl === '') {
    return ''
  }
  if (envUrl) {
    // If we're on HTTPS but env var is HTTP, upgrade to HTTPS
    if (typeof window !== 'undefined' && window.location.protocol === 'https:' && envUrl.startsWith('http://')) {
      return envUrl.replace('http://', 'https://')
    }
    return envUrl
  }
  // Default for local development
  return 'http://localhost:8000'
}

const API_BASE_URL = getApiBaseUrl()

// Log API URL for debugging
console.log('API_BASE_URL:', API_BASE_URL)
console.log('VITE_API_URL env:', import.meta.env.VITE_API_URL)

class APIClient {
  private client: AxiosInstance

  // Single-flight refresh state: if multiple requests 401 at the same time,
  // only ONE /auth/refresh call is made; the rest await the same promise.
  private refreshPromise: Promise<string | null> | null = null

  constructor() {
    console.log('APIClient initializing with baseURL:', API_BASE_URL)
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('access_token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any
        const status = error.response?.status
        const url = originalRequest?.url || ''

        if (status === 401) {
          // Only these endpoints should NEVER trigger a refresh attempt:
          //   - /auth/refresh itself (infinite loop protection — though the
          //     refresh call uses a bare axios instance that bypasses this
          //     interceptor entirely, so this is belt-and-braces)
          //   - /auth/google and /auth/callback (login redirect, not authed)
          //   - /auth/login (no session yet)
          //
          // /auth/me, /auth/logout, and all feature endpoints SHOULD trigger
          // a refresh — that's the whole point of this fix.
          const noRefreshPaths = [
            '/auth/refresh',
            '/auth/google',
            '/auth/callback',
            '/auth/login',
          ]
          const shouldSkipRefresh = noRefreshPaths.some((p) => url.includes(p))

          // Avoid infinite retry loops: only retry once per original request.
          if (!shouldSkipRefresh && originalRequest && !originalRequest._retry) {
            originalRequest._retry = true
            try {
              const newToken = await this.getFreshAccessToken()
              if (newToken) {
                // Retry the original request with the new token
                originalRequest.headers = originalRequest.headers || {}
                originalRequest.headers.Authorization = `Bearer ${newToken}`
                return this.client(originalRequest)
              }
            } catch {
              // Fall through to the redirect below
            }
            // Refresh failed — clear tokens and bounce to login
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
            window.location.href = '/login'
          } else if (shouldSkipRefresh) {
            // 401 directly from the unauthenticated endpoints → kick out
            localStorage.removeItem('access_token')
            localStorage.removeItem('refresh_token')
            window.location.href = '/login'
          }
        }

        // Report to OCS telemetry
        const reportableStatuses = [401, 403, 429, 500, 502, 503, 504];
        if (error.response?.status && reportableStatuses.includes(error.response.status)) {
          import('@/lib/error-reporter').then(({ reportApiError }) => {
            reportApiError(
              error.config?.url || 'unknown',
              error.response!.status,
              String(error.response?.data || error.message),
            );
          }).catch(() => {});
        }

        return Promise.reject(error)
      }
    )
  }

  /**
   * Get a fresh access token using the stored refresh token.
   *
   * Implements single-flight: concurrent callers share the same in-flight
   * refresh promise so only ONE POST /auth/refresh is made even if 20
   * requests hit 401 simultaneously. The new token is stored in
   * localStorage before the promise resolves.
   *
   * Returns the new access token, or null if no refresh token exists.
   * Throws if the refresh request itself fails (caller handles by redirecting
   * to login).
   */
  private async getFreshAccessToken(): Promise<string | null> {
    if (this.refreshPromise) return this.refreshPromise

    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) return null

    this.refreshPromise = (async () => {
      try {
        // Use a bare axios instance so this call doesn't re-enter the
        // interceptor (which would cause an infinite loop on failure).
        const resp = await axios.post(
          `${API_BASE_URL}/api/v1/auth/refresh`,
          { refresh_token: refreshToken },
          { headers: { 'Content-Type': 'application/json' } },
        )
        const newToken: string | undefined = resp.data?.access_token
        if (!newToken) return null
        localStorage.setItem('access_token', newToken)
        return newToken
      } finally {
        // Clear the in-flight promise regardless of outcome
        this.refreshPromise = null
      }
    })()

    return this.refreshPromise
  }

  // Auth endpoints - Google OAuth
  async getGoogleAuthUrl(): Promise<GoogleAuthUrlResponse> {
    const response = await this.client.get<GoogleAuthUrlResponse>('/api/v1/auth/google')
    return response.data
  }

  async getCurrentUser(): Promise<User> {
    const response = await this.client.get<User>('/api/v1/auth/me')
    return response.data
  }

  async logout(): Promise<void> {
    await this.client.post('/api/v1/auth/logout')
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('oauth_state')
  }

  // User endpoints
  async getUsers(params?: { skip?: number; limit?: number }): Promise<PaginatedResponse<User>> {
    const response = await this.client.get<PaginatedResponse<User>>('/api/v1/users', { params })
    return response.data
  }

  async getUser(id: string): Promise<User> {
    const response = await this.client.get<User>(`/api/v1/users/${id}`)
    return response.data
  }

  async updateUser(id: string, data: Partial<User>): Promise<User> {
    const response = await this.client.patch<User>(`/api/v1/users/${id}`, data)
    return response.data
  }

  async deleteUser(id: string): Promise<void> {
    await this.client.delete(`/api/v1/users/${id}`)
  }

  // Workspace endpoints
  async getWorkspaces(params?: { skip?: number; limit?: number }): Promise<PaginatedResponse<Workspace>> {
    const response = await this.client.get<PaginatedResponse<Workspace>>('/api/v1/workspaces', { params })
    return response.data
  }

  async getWorkspace(id: string): Promise<Workspace> {
    const response = await this.client.get<Workspace>(`/api/v1/workspaces/${id}`)
    return response.data
  }

  async createWorkspace(data: WorkspaceCreate): Promise<Workspace> {
    const response = await this.client.post<Workspace>('/api/v1/workspaces', data)
    return response.data
  }

  async updateWorkspace(id: string, data: Partial<WorkspaceCreate>): Promise<Workspace> {
    const response = await this.client.patch<Workspace>(`/api/v1/workspaces/${id}`, data)
    return response.data
  }

  async deleteWorkspace(id: string): Promise<void> {
    await this.client.delete(`/api/v1/workspaces/${id}`)
  }

  // Agent endpoints
  async getAgents(params?: { workspace_id?: string; skip?: number; limit?: number }): Promise<PaginatedResponse<Agent>> {
    const response = await this.client.get<PaginatedResponse<Agent>>('/api/v1/agents', { params })
    return response.data
  }

  async getAgent(id: string): Promise<Agent> {
    const response = await this.client.get<Agent>(`/api/v1/agents/${id}`)
    return response.data
  }

  async createAgent(data: AgentCreate): Promise<Agent> {
    const response = await this.client.post<Agent>('/api/v1/agents/', data)
    return response.data
  }

  async updateAgent(id: string, data: Partial<AgentCreate>): Promise<Agent> {
    const response = await this.client.patch<Agent>(`/api/v1/agents/${id}`, data)
    return response.data
  }

  async deleteAgent(id: string): Promise<void> {
    await this.client.delete(`/api/v1/agents/${id}`)
  }

  // Workflow endpoints
  async getWorkflows(params?: { workspace_id?: string; skip?: number; limit?: number }): Promise<PaginatedResponse<Workflow>> {
    const response = await this.client.get<PaginatedResponse<Workflow>>('/api/v1/workflows', { params })
    return response.data
  }

  async getWorkflow(id: string): Promise<Workflow> {
    const response = await this.client.get<Workflow>(`/api/v1/workflows/${id}`)
    return response.data
  }

  async createWorkflow(data: WorkflowCreate): Promise<Workflow> {
    const response = await this.client.post<Workflow>('/api/v1/workflows', data)
    return response.data
  }

  async updateWorkflow(id: string, data: Partial<WorkflowCreate>): Promise<Workflow> {
    const response = await this.client.patch<Workflow>(`/api/v1/workflows/${id}`, data)
    return response.data
  }

  async deleteWorkflow(id: string): Promise<void> {
    await this.client.delete(`/api/v1/workflows/${id}`)
  }

  // Execution endpoints
  async getExecutions(params?: {
    agent_id?: string
    status?: string
    skip?: number
    limit?: number
  }): Promise<PaginatedResponse<Execution>> {
    const response = await this.client.get<PaginatedResponse<Execution>>('/api/v1/executions', { params })
    return response.data
  }

  async getExecution(id: string): Promise<Execution> {
    const response = await this.client.get<Execution>(`/api/v1/executions/${id}`)
    return response.data
  }

  async createExecution(data: ExecutionCreate): Promise<Execution> {
    const response = await this.client.post<Execution>('/api/v1/executions', data)
    return response.data
  }

  async cancelExecution(id: string): Promise<Execution> {
    const response = await this.client.post<Execution>(`/api/v1/executions/${id}/cancel`)
    return response.data
  }

  async deleteExecution(id: string): Promise<void> {
    await this.client.delete(`/api/v1/executions/${id}`)
  }

  async getExecutionStats(agentId?: string): Promise<ExecutionStats> {
    const params = agentId ? { agent_id: agentId } : {}
    const response = await this.client.get<ExecutionStats>('/api/v1/executions/stats/summary', { params })
    return response.data
  }

  // MCP endpoints
  async getMCPServers(): Promise<MCPServer[]> {
    const response = await this.client.get<MCPServer[]>('/api/v1/mcp/servers')
    return response.data
  }

  async getMCPServer(id: string): Promise<MCPServer> {
    const response = await this.client.get<MCPServer>(`/api/v1/mcp/servers/${id}`)
    return response.data
  }

  async registerMCPServer(data: MCPServerCreate): Promise<MCPServer> {
    const response = await this.client.post<MCPServer>('/api/v1/mcp/servers', data)
    return response.data
  }

  async unregisterMCPServer(id: string): Promise<void> {
    await this.client.delete(`/api/v1/mcp/servers/${id}`)
  }

  async callMCPTool(data: MCPToolCall): Promise<any> {
    const response = await this.client.post('/api/v1/mcp/call', data)
    return response.data
  }

  async checkMCPServerHealth(id: string): Promise<any> {
    const response = await this.client.get(`/api/v1/mcp/servers/${id}/health`)
    return response.data
  }

  // Knowledge Base / Vector DB endpoints
  async getCollections(): Promise<string[]> {
    const response = await this.client.get<string[]>('/api/v1/knowledge/collections')
    return response.data
  }

  async getCollection(name: string): Promise<Collection> {
    const response = await this.client.get<Collection>(`/api/v1/knowledge/collections/${name}`)
    return response.data
  }

  async createCollection(data: CollectionCreate): Promise<Collection> {
    const response = await this.client.post<Collection>('/api/v1/knowledge/collections', data)
    return response.data
  }

  async deleteCollection(name: string): Promise<void> {
    await this.client.delete(`/api/v1/knowledge/collections/${name}`)
  }

  async insertDocuments(collectionName: string, documents: Document[]): Promise<any> {
    const response = await this.client.post(`/api/v1/knowledge/collections/${collectionName}/documents`, {
      documents,
    })
    return response.data
  }

  async searchDocuments(
    collectionName: string,
    query: string,
    params?: {
      limit?: number
      score_threshold?: number
      filter_conditions?: Record<string, any>
    }
  ): Promise<{ query: string; results: SearchResult[]; total: number }> {
    const response = await this.client.post(`/api/v1/knowledge/collections/${collectionName}/search`, {
      query,
      ...params,
    })
    return response.data
  }

  async ragRetrieve(data: RAGRequest): Promise<RAGResponse> {
    const response = await this.client.post<RAGResponse>('/api/v1/knowledge/rag', data)
    return response.data
  }

  // GraphRAG endpoints (Weaviate + Memgraph hybrid retrieval)
  async getGraphRAGStatus(): Promise<{
    available: boolean
    message: string
    weaviate_connected: boolean
    memgraph_connected: boolean
  }> {
    const response = await this.client.get('/api/v1/knowledge/graph-rag/status')
    return response.data
  }

  async queryGraphRAG(params: {
    query: string
    graph_id?: string
    project_id?: string
    agent_id?: string
    mode?: 'semantic_only' | 'graph_only' | 'hybrid' | 'graph_enhanced'
    semantic_limit?: number
    graph_depth?: number
    include_memories?: boolean
  }): Promise<{
    query: string
    mode: string
    documents: any[]
    entities: any[]
    relationships: any[]
    temporal_facts: any[]
    agent_memories: any[]
    agent_decisions: any[]
    total_results: number
    processing_time_ms: number
    context_string?: string
  }> {
    const response = await this.client.post('/api/v1/knowledge/graph-rag/query', {
      query: params.query,
      graph_id: params.graph_id || 'default',
      project_id: params.project_id,
      agent_id: params.agent_id,
      mode: params.mode || 'hybrid',
      semantic_limit: params.semantic_limit || 10,
      graph_depth: params.graph_depth || 2,
      include_memories: params.include_memories ?? true,
    })
    return response.data
  }

  async addGraphRAGEntity(params: {
    graph_id?: string
    name: string
    entity_type?: string
    description?: string
    properties?: Record<string, any>
    project_id?: string
  }): Promise<{ success: boolean; entity_id: string; message: string }> {
    const response = await this.client.post('/api/v1/knowledge/graph-rag/entities', {
      graph_id: params.graph_id || 'default',
      name: params.name,
      entity_type: params.entity_type || 'concept',
      description: params.description,
      properties: params.properties,
      project_id: params.project_id,
    })
    return response.data
  }

  async addGraphRAGRelationship(params: {
    graph_id?: string
    from_entity_id: string
    to_entity_id: string
    relationship_type: string
    properties?: Record<string, any>
  }): Promise<{ success: boolean; relationship_id: string; message: string }> {
    const response = await this.client.post('/api/v1/knowledge/graph-rag/relationships', params)
    return response.data
  }

  async storeAgentMemory(params: {
    graph_id?: string
    agent_id: string
    content: string
    memory_type?: 'episodic' | 'semantic' | 'procedural' | 'decision'
    context?: Record<string, any>
    importance?: number
    project_id?: string
  }): Promise<{ success: boolean; memory_id: string; message: string }> {
    const response = await this.client.post('/api/v1/knowledge/graph-rag/agent-memories', {
      graph_id: params.graph_id || 'default',
      agent_id: params.agent_id,
      content: params.content,
      memory_type: params.memory_type || 'episodic',
      context: params.context,
      importance: params.importance ?? 0.5,
      project_id: params.project_id,
    })
    return response.data
  }

  async getAgentMemories(
    agentId: string,
    params?: { graph_id?: string; memory_type?: string; limit?: number }
  ): Promise<{ agent_id: string; graph_id: string; memories: any[]; total: number }> {
    const response = await this.client.get(`/api/v1/knowledge/graph-rag/agent-memories/${agentId}`, { params })
    return response.data
  }

  async getAgentDecisions(
    agentId: string,
    params?: { graph_id?: string; limit?: number }
  ): Promise<{ agent_id: string; graph_id: string; decisions: any[]; total: number }> {
    const response = await this.client.get(`/api/v1/knowledge/graph-rag/agent-decisions/${agentId}`, { params })
    return response.data
  }

  async listGraphRAGEntities(
    graphId: string,
    params?: { entity_type?: string; limit?: number }
  ): Promise<{ graph_id: string; entities: any[]; total: number }> {
    const response = await this.client.get(`/api/v1/knowledge/graph-rag/entities/${graphId}`, { params })
    return response.data
  }

  async listGraphRAGRelationships(
    graphId: string,
    params?: { relationship_type?: string; limit?: number }
  ): Promise<{ graph_id: string; relationships: any[]; total: number }> {
    const response = await this.client.get(`/api/v1/knowledge/graph-rag/relationships/${graphId}`, { params })
    return response.data
  }

  // Team endpoints
  async getTeams(params?: { workspace_id?: string; skip?: number; limit?: number }): Promise<PaginatedResponse<AgentTeam>> {
    const response = await this.client.get<PaginatedResponse<AgentTeam>>('/api/v1/teams', { params })
    return response.data
  }

  async getTeam(id: string): Promise<AgentTeam> {
    const response = await this.client.get<AgentTeam>(`/api/v1/teams/${id}`)
    return response.data
  }

  async createTeam(data: AgentTeamCreate): Promise<AgentTeam> {
    const response = await this.client.post<AgentTeam>('/api/v1/teams', data)
    return response.data
  }

  async updateTeam(id: string, data: AgentTeamUpdate): Promise<AgentTeam> {
    const response = await this.client.patch<AgentTeam>(`/api/v1/teams/${id}`, data)
    return response.data
  }

  async deleteTeam(id: string): Promise<void> {
    await this.client.delete(`/api/v1/teams/${id}`)
  }

  async addTeamMember(teamId: string, data: TeamMemberCreate): Promise<any> {
    const response = await this.client.post(`/api/v1/teams/${teamId}/members`, data)
    return response.data
  }

  async removeTeamMember(teamId: string, memberId: string): Promise<void> {
    await this.client.delete(`/api/v1/teams/${teamId}/members/${memberId}`)
  }

  async executeTeam(teamId: string, inputData: Record<string, any>): Promise<TeamExecution> {
    const response = await this.client.post<TeamExecution>(`/api/v1/teams/${teamId}/execute`, {
      input_data: inputData,
    })
    return response.data
  }

  async getTeamExecutions(teamId: string, params?: { skip?: number; limit?: number }): Promise<PaginatedResponse<TeamExecution>> {
    const response = await this.client.get<PaginatedResponse<TeamExecution>>(`/api/v1/teams/${teamId}/executions`, { params })
    return response.data
  }

  async getTeamExecution(executionId: string): Promise<TeamExecution> {
    const response = await this.client.get<TeamExecution>(`/api/v1/teams/executions/${executionId}`)
    return response.data
  }

  async cancelTeamExecution(executionId: string): Promise<TeamExecution> {
    const response = await this.client.post<TeamExecution>(`/api/v1/teams/executions/${executionId}/cancel`)
    return response.data
  }

  async getExecutionMessages(executionId: string): Promise<AgentMessage[]> {
    const response = await this.client.get<AgentMessage[]>(`/api/v1/teams/executions/${executionId}/messages`)
    return response.data
  }

  // Knowledge Graph endpoints
  async getKnowledgeGraphs(params?: {
    workspace_id?: string
    graph_type?: string
    status?: string
    skip?: number
    limit?: number
  }): Promise<PaginatedResponse<KnowledgeGraph>> {
    const response = await this.client.get<PaginatedResponse<KnowledgeGraph>>('/api/v1/graphs', { params })
    return response.data
  }

  async createKnowledgeGraph(data: KnowledgeGraphCreate): Promise<KnowledgeGraph> {
    const response = await this.client.post<KnowledgeGraph>('/api/v1/graphs', data)
    return response.data
  }

  async getKnowledgeGraph(graphId: string): Promise<KnowledgeGraph> {
    const response = await this.client.get<KnowledgeGraph>(`/api/v1/graphs/${graphId}`)
    return response.data
  }

  async updateKnowledgeGraph(graphId: string, data: KnowledgeGraphUpdate): Promise<KnowledgeGraph> {
    const response = await this.client.patch<KnowledgeGraph>(`/api/v1/graphs/${graphId}`, data)
    return response.data
  }

  async deleteKnowledgeGraph(graphId: string): Promise<void> {
    await this.client.delete(`/api/v1/graphs/${graphId}`)
  }

  // Graph Node endpoints
  async createGraphNode(graphId: string, data: GraphNodeCreate): Promise<GraphNode> {
    const response = await this.client.post<GraphNode>(`/api/v1/graphs/${graphId}/nodes`, data)
    return response.data
  }

  async getGraphNodes(graphId: string, params?: { node_types?: NodeType[]; limit?: number }): Promise<GraphNode[]> {
    const response = await this.client.get<GraphNode[]>(`/api/v1/graphs/${graphId}/nodes`, { params })
    return response.data
  }

  async getGraphNode(graphId: string, nodeId: string): Promise<GraphNode> {
    const response = await this.client.get<GraphNode>(`/api/v1/graphs/${graphId}/nodes/${nodeId}`)
    return response.data
  }

  async updateGraphNode(graphId: string, nodeId: string, data: GraphNodeUpdate): Promise<GraphNode> {
    const response = await this.client.patch<GraphNode>(`/api/v1/graphs/${graphId}/nodes/${nodeId}`, data)
    return response.data
  }

  async deleteGraphNode(graphId: string, nodeId: string): Promise<void> {
    await this.client.delete(`/api/v1/graphs/${graphId}/nodes/${nodeId}`)
  }

  // Graph Relationship endpoints
  async createGraphRelationship(graphId: string, data: GraphRelationshipCreate): Promise<GraphRelationship> {
    const response = await this.client.post<GraphRelationship>(`/api/v1/graphs/${graphId}/relationships`, data)
    return response.data
  }

  async getGraphRelationships(graphId: string, params?: { relation_types?: RelationType[]; limit?: number }): Promise<GraphRelationship[]> {
    const response = await this.client.get<GraphRelationship[]>(`/api/v1/graphs/${graphId}/relationships`, { params })
    return response.data
  }

  async deleteGraphRelationship(graphId: string, relationshipId: string): Promise<void> {
    await this.client.delete(`/api/v1/graphs/${graphId}/relationships/${relationshipId}`)
  }

  // Graph Query endpoints
  async searchGraphNodes(graphId: string, data: GraphSearchRequest): Promise<GraphSearchResponse> {
    const response = await this.client.post<GraphSearchResponse>(`/api/v1/graphs/${graphId}/search`, data)
    return response.data
  }

  async findGraphPath(graphId: string, data: GraphPathRequest): Promise<GraphPathResponse> {
    const response = await this.client.post<GraphPathResponse>(`/api/v1/graphs/${graphId}/path`, data)
    return response.data
  }

  async getGraphNeighbors(graphId: string, nodeId: string, params?: { direction?: string; limit?: number }): Promise<GraphNode[]> {
    const response = await this.client.get<GraphNode[]>(`/api/v1/graphs/${graphId}/neighbors/${nodeId}`, { params })
    return response.data
  }

  async getGraphVisualization(graphId: string, data: {
    root_node_id?: string
    depth?: number
    node_types?: NodeType[]
    limit?: number
  }): Promise<GraphVisualizationData> {
    const response = await this.client.post<GraphVisualizationData>(`/api/v1/graphs/${graphId}/visualize`, data)
    return response.data
  }

  async getGraphStatistics(graphId: string): Promise<GraphStatistics> {
    const response = await this.client.get<GraphStatistics>(`/api/v1/graphs/${graphId}/statistics`)
    return response.data
  }

  // Agent-Graph Association endpoints
  async associateAgentWithGraph(graphId: string, data: { agent_id: string; can_read: boolean; can_write: boolean; context?: Record<string, any> }): Promise<AgentGraphAssociation> {
    const response = await this.client.post<AgentGraphAssociation>(`/api/v1/graphs/${graphId}/agents`, data)
    return response.data
  }

  async removeAgentFromGraph(graphId: string, agentId: string): Promise<void> {
    await this.client.delete(`/api/v1/graphs/${graphId}/agents/${agentId}`)
  }

  async getAgentGraphs(agentId: string): Promise<KnowledgeGraph[]> {
    const response = await this.client.get<KnowledgeGraph[]>(`/api/v1/graphs/agents/${agentId}/graphs`)
    return response.data
  }

  // Team-Graph Association endpoints
  async associateTeamWithGraph(graphId: string, data: { team_id: string; can_read: boolean; can_write: boolean; context?: Record<string, any> }): Promise<TeamGraphAssociation> {
    const response = await this.client.post<TeamGraphAssociation>(`/api/v1/graphs/${graphId}/teams`, data)
    return response.data
  }

  async removeTeamFromGraph(graphId: string, teamId: string): Promise<void> {
    await this.client.delete(`/api/v1/graphs/${graphId}/teams/${teamId}`)
  }

  async getTeamGraphs(teamId: string): Promise<KnowledgeGraph[]> {
    const response = await this.client.get<KnowledgeGraph[]>(`/api/v1/graphs/teams/${teamId}/graphs`)
    return response.data
  }

  // HPC endpoints - generic methods for HPC module
  async hpcGet<T>(path: string, params?: Record<string, any>): Promise<T> {
    const response = await this.client.get<T>(`/api/v1/hpc${path}`, { params })
    return response.data
  }

  async hpcPost<T>(path: string, data?: any): Promise<T> {
    const response = await this.client.post<T>(`/api/v1/hpc${path}`, data)
    return response.data
  }

  async hpcPatch<T>(path: string, data?: any): Promise<T> {
    const response = await this.client.patch<T>(`/api/v1/hpc${path}`, data)
    return response.data
  }

  async hpcDelete(path: string): Promise<void> {
    await this.client.delete(`/api/v1/hpc${path}`)
  }

  // ==================== Tools API ====================

  /**
   * Get list of all available tools
   */
  async getTools(params?: { category?: string; server_type?: string }): Promise<ToolListResponse> {
    const response = await this.client.get<ToolListResponse>('/api/v1/tools/', { params })
    return response.data
  }

  /**
   * Get list of tool categories
   */
  async getToolCategories(): Promise<string[]> {
    const response = await this.client.get<string[]>('/api/v1/tools/categories')
    return response.data
  }

  /**
   * Get details of a specific tool
   */
  async getTool(toolName: string): Promise<ToolInfo> {
    const response = await this.client.get<ToolInfo>(`/api/v1/tools/${toolName}`)
    return response.data
  }

  /**
   * Execute a tool
   */
  async executeTool(request: ToolExecutionRequest): Promise<ToolExecutionResponse> {
    const response = await this.client.post<ToolExecutionResponse>('/api/v1/tools/execute', request)
    return response.data
  }

  /**
   * Execute multiple tools in batch
   */
  async executeToolsBatch(requests: ToolExecutionRequest[]): Promise<ToolExecutionResponse[]> {
    const response = await this.client.post<ToolExecutionResponse[]>('/api/v1/tools/batch', requests)
    return response.data
  }

  /**
   * Get tool configuration for an agent
   */
  async getAgentToolConfig(agentId: string): Promise<AgentToolConfig> {
    const response = await this.client.get<AgentToolConfig>(`/api/v1/tools/agent/${agentId}/config`)
    return response.data
  }

  /**
   * Update tool configuration for an agent
   */
  async updateAgentToolConfig(agentId: string, config: AgentToolConfig): Promise<AgentToolConfig> {
    const response = await this.client.put<AgentToolConfig>(`/api/v1/tools/agent/${agentId}/config`, config)
    return response.data
  }

  /**
   * Get tool usage statistics for an agent
   */
  async getAgentToolStats(agentId: string): Promise<ToolStatsResponse> {
    const response = await this.client.get<ToolStatsResponse>(`/api/v1/tools/agent/${agentId}/stats`)
    return response.data
  }

  // ==================== Inbox API ====================

  /**
   * Get channels for a project
   */
  async getProjectChannels(projectId: string, includeAgentChannels = true): Promise<any[]> {
    const response = await this.client.get(`/api/v1/inbox/projects/${projectId}/channels`, {
      params: { include_agent_channels: includeAgentChannels }
    })
    return response.data
  }

  /**
   * Get channels for a workspace/user
   */
  async getUserChannels(workspaceId: string): Promise<Array<{ channel: any; unread_count: number; is_muted: boolean }>> {
    const response = await this.client.get(`/api/v1/inbox/workspaces/${workspaceId}/channels`)
    return response.data
  }

  /**
   * Create or get a channel for a project
   */
  async createOrGetChannel(projectId: string, workspaceId: string, agentId?: string): Promise<any> {
    const response = await this.client.post(`/api/v1/inbox/projects/${projectId}/channels`, null, {
      params: { workspace_id: workspaceId, agent_id: agentId }
    })
    return response.data
  }

  /**
   * Get messages for a channel
   */
  async getChannelMessages(channelId: string, params?: { limit?: number; before_id?: string }): Promise<any[]> {
    const response = await this.client.get(`/api/v1/inbox/channels/${channelId}/messages`, { params })
    return response.data
  }

  /**
   * Send a message to a channel
   */
  async sendChannelMessage(channelId: string, projectId: string, data: {
    content: string
    message_type?: string
    parent_message_id?: string
    priority?: string
    attachments?: any[]
    code_blocks?: Array<{ language: string; code: string }>
    mentioned_user_ids?: string[]
    mentioned_agent_ids?: string[]
    metadata?: Record<string, any>
  }): Promise<any> {
    const response = await this.client.post(`/api/v1/inbox/channels/${channelId}/messages`, data, {
      params: { project_id: projectId }
    })
    return response.data
  }

  /**
   * Get thread messages
   */
  async getThreadMessages(threadRootId: string): Promise<any[]> {
    const response = await this.client.get(`/api/v1/inbox/threads/${threadRootId}/messages`)
    return response.data
  }

  /**
   * Respond to a HITL message
   */
  async respondToHITL(messageId: string, responseData: Record<string, any>): Promise<any> {
    const response = await this.client.post(`/api/v1/inbox/messages/${messageId}/respond`, { response: responseData })
    return response.data
  }

  /**
   * Add reaction to a message
   */
  async addMessageReaction(messageId: string, emoji: string): Promise<void> {
    await this.client.post(`/api/v1/inbox/messages/${messageId}/reactions`, { emoji })
  }

  /**
   * Remove reaction from a message
   */
  async removeMessageReaction(messageId: string, emoji: string): Promise<void> {
    await this.client.delete(`/api/v1/inbox/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`)
  }

  /**
   * Mark channel as read
   */
  async markChannelRead(channelId: string): Promise<void> {
    await this.client.post(`/api/v1/inbox/channels/${channelId}/read`)
  }

  /**
   * Get unread counts
   */
  async getUnreadCounts(projectId?: string): Promise<{ counts: Record<string, number> }> {
    const response = await this.client.get('/api/v1/inbox/unread-counts', {
      params: { project_id: projectId }
    })
    return response.data
  }

  /**
   * Get notifications
   */
  async getNotifications(params?: { unread_only?: boolean; limit?: number }): Promise<any[]> {
    const response = await this.client.get('/api/v1/inbox/notifications', { params })
    return response.data
  }

  /**
   * Mark notification as read
   */
  async markNotificationRead(notificationId: string): Promise<void> {
    await this.client.post(`/api/v1/inbox/notifications/${notificationId}/read`)
  }

  /**
   * Mark all notifications as read
   */
  async markAllNotificationsRead(projectId?: string): Promise<void> {
    await this.client.post('/api/v1/inbox/notifications/read-all', null, {
      params: { project_id: projectId }
    })
  }

  // ==================== Crews API ====================

  /**
   * Get list of all crews
   */
  async getCrews(params?: { domain?: string }): Promise<Crew[]> {
    const response = await this.client.get<Crew[]>('/api/v1/crews', { params })
    return response.data
  }

  /**
   * Get a specific crew by name
   */
  async getCrew(name: string): Promise<Crew> {
    const response = await this.client.get<Crew>(`/api/v1/crews/${name}`)
    return response.data
  }

  /**
   * Invoke a crew
   */
  async invokeCrew(name: string, request: CrewInvokeRequest): Promise<CrewInvokeResponse> {
    const response = await this.client.post<CrewInvokeResponse>(
      `/api/v1/crews/${name}/invoke`,
      request
    )
    return response.data
  }

  // ==================== Orchestrator (auto-orchestrator) ====================

  /**
   * Invoke the auto-orchestrator (or any agent) with a goal prompt.
   *
   * Per PLAN-orchestrator-ux.md Phase A — single-shot invoke. For streaming
   * phase-by-phase progress (decompose / execute / verify / synthesize) use
   * ``streamOrchestrator(...)`` below; the ``OrchestratorComposer`` does that
   * internally via ``useOrchestratorStream``.
   */
  async invokeOrchestrator(
    prompt: string,
    context: Record<string, any> = {},
    agentName: string = 'auto-orchestrator',
  ): Promise<{ result: any; request_id?: string; metadata?: any; audit?: any }> {
    const response = await this.client.post(`/api/v1/agents/${agentName}/invoke`, { prompt, context })
    return response.data
  }

  /**
   * Open an SSE stream for an orchestrator run. Returns the underlying
   * ``Response`` object so callers can either pipe ``response.body`` into
   * a ``ReadableStream`` reader (what ``useOrchestratorStream`` does) or
   * await text for tests. Per PLAN-orchestrator-ux.md Phase A.
   */
  async streamOrchestrator(
    prompt: string,
    context: Record<string, any> = {},
    agentName: string = 'auto-orchestrator',
    init?: RequestInit,
  ): Promise<Response> {
    const baseURL = this.client.defaults.baseURL || ''
    const token = (this.client.defaults.headers.common as Record<string, string>)?.Authorization
    return fetch(`${baseURL}/api/v1/agents/${agentName}/invoke/stream`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: token } : {}),
        ...((init?.headers as Record<string, string>) || {}),
      },
      body: JSON.stringify({ prompt, context }),
      ...(init || {}),
    })
  }

  /**
   * Recent auto-orchestrator runs (for the Dashboard "Recent runs" rail).
   * Hits ``/api/v1/executions`` with an agent_name filter — execution_log
   * already records every run with agent_name, request_id, and timing.
   * Per PLAN-orchestrator-ux.md Phase C.
   */
  async getRecentOrchestratorRuns(
    limit = 10,
    agentName: string = 'auto-orchestrator',
  ): Promise<Array<{
    request_id: string
    agent_name: string
    prompt?: string
    output_summary?: string
    success: boolean
    duration_ms?: number
    total_tokens?: number
    total_cost_usd?: number
    created_at?: string
  }>> {
    const response = await this.client.get('/api/v1/executions', {
      params: { agent_name: agentName, limit },
    })
    const items = response.data?.items || response.data?.executions || response.data || []
    return Array.isArray(items) ? items : []
  }

  /**
   * Enable a crew
   */
  async enableCrew(name: string): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>(`/api/v1/crews/${name}/enable`)
    return response.data
  }

  /**
   * Disable a crew
   */
  async disableCrew(name: string): Promise<{ message: string }> {
    const response = await this.client.post<{ message: string }>(`/api/v1/crews/${name}/disable`)
    return response.data
  }

  /**
   * Get agents in a crew
   */
  async getCrewAgents(name: string): Promise<{ crew: string; agents: Array<{ name: string; role: string }> }> {
    const response = await this.client.get<{ crew: string; agents: Array<{ name: string; role: string }> }>(
      `/api/v1/crews/${name}/agents`
    )
    return response.data
  }

  /**
   * Get crew graph structure for visualization
   */
  async getCrewGraph(name: string): Promise<{
    crew_name: string
    display_name: string
    version: string
    process_type: 'sequential' | 'parallel' | 'hierarchical'
    nodes: Array<{
      id: string
      type: string
      label: string
      description: string
      status: string
      position: { x: number; y: number }
    }>
    edges: Array<{
      id: string
      source: string
      target: string
      condition?: string
    }>
    metadata: {
      domain: string
      agent_count: number
      agents: Array<{ name: string; role: string }>
    }
  }> {
    const response = await this.client.get(`/api/v1/crews/${name}/graph`)
    return response.data
  }

  // ==================== N8N Workflows API ====================

  /**
   * Get list of workflows from n8n service
   */
  async getN8NWorkflows(params?: { active?: boolean; limit?: number }): Promise<Array<{
    id: string
    name: string
    active: boolean
    createdAt?: string
    updatedAt?: string
    nodes?: Array<{ type: string; name: string; parameters?: Record<string, any> }>
  }>> {
    const response = await this.client.get('/api/v1/n8n/workflows', { params })
    return response.data
  }

  /**
   * Execute an n8n workflow
   */
  async executeN8NWorkflow(workflowId: string, inputData?: Record<string, any>): Promise<{
    executionId: string
    status: string
  }> {
    const response = await this.client.post(`/api/v1/n8n/workflows/${workflowId}/execute`, inputData)
    return response.data
  }

  // ==================== Centralized Agents API ====================

  /**
   * Get list of centralized agents
   */
  async getCentralizedAgents(params?: { domain?: string }): Promise<Array<{
    name: string
    display_name: string
    description: string
    domain: string
    enabled: boolean
    version?: string
    capabilities?: Record<string, any>
  }>> {
    const response = await this.client.get('/api/v1/agents/centralized', { params })
    return response.data
  }

  /**
   * Fetch execution history from execution_log table (permanent records).
   */
  async getExecutionHistory(params?: {
    agent_name?: string
    success?: boolean
    source?: string
    orchestration_id?: string
    days?: number
    skip?: number
    limit?: number
  }): Promise<{ total: number; data: any[] }> {
    const response = await this.client.get('/api/v1/executions/history', { params })
    return response.data
  }

  /**
   * Get full detail for a single execution.
   */
  async getExecutionDetail(requestId: string): Promise<any> {
    const response = await this.client.get(`/api/v1/executions/history/${requestId}`)
    return response.data
  }

  /**
   * Get orchestration detail: parent + sub-tasks + task DAG.
   */
  async getOrchestrationDetail(requestId: string): Promise<{
    is_orchestration: boolean
    parent: any
    task_dag?: any
    verification_verdict?: string
    sub_tasks: any[]
  }> {
    const response = await this.client.get(`/api/v1/executions/history/${requestId}/orchestration`)
    return response.data
  }

  /**
   * Invoke a centralized agent with multi-modal input (Phase 5).
   *
   * Wraps POST /api/v1/agents/{name}/invoke/multimodal which accepts
   * multipart/form-data. Supply only the files relevant to the agent's
   * supported_modalities — the backend rejects unsupported modalities
   * with 400.
   */
  async invokeCentralizedAgentMultimodal(
    agentName: string,
    params: {
      prompt: string
      context?: Record<string, any>
      image?: File | null
      audio?: File | null
      video?: File | null
    },
  ): Promise<Record<string, any>> {
    const fd = new FormData()
    fd.append('prompt', params.prompt)
    if (params.context) fd.append('context_json', JSON.stringify(params.context))
    if (params.image) fd.append('image', params.image)
    if (params.audio) fd.append('audio', params.audio)
    if (params.video) fd.append('video', params.video)
    const response = await this.client.post(
      `/api/v1/agents/${agentName}/invoke/multimodal`,
      fd,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        // Long uploads (video): override default timeout to 10 min
        timeout: 10 * 60 * 1000,
      },
    )
    return response.data
  }

  /**
   * Create a new centralized Ziro agent via the Create Agent Wizard (Phase 3b).
   * The backend generates code, writes files, and patches metadata.
   * Caller must trigger a backend restart for the new agent to become active.
   */
  async createCentralizedAgent(payload: Record<string, any>): Promise<{
    success: boolean
    name: string
    file_path: string
    relative_import: string
    class_name: string
    restart_required: boolean
    audit_passed: boolean
    gaps: Record<string, string[]>
    message: string
  }> {
    const response = await this.client.post('/api/v1/agents/centralized/create', payload)
    return response.data
  }

  /**
   * Get a specific centralized agent
   */
  async getCentralizedAgent(name: string): Promise<{
    name: string
    display_name: string
    description: string
    domain: string
    enabled: boolean
    version?: string
    capabilities?: Record<string, any>
  }> {
    const response = await this.client.get(`/api/v1/agents/centralized/${name}`)
    return response.data
  }

  /**
   * Get agent graph structure for visualization
   */
  async getAgentGraph(name: string): Promise<AgentGraphResponse> {
    const response = await this.client.get<AgentGraphResponse>(`/api/v1/agents/centralized/${name}/graph`)
    return response.data
  }

  /**
   * Get agent execution graph state
   */
  async getAgentExecutionGraph(name: string, executionId: string): Promise<LangGraphState> {
    const response = await this.client.get<LangGraphState>(
      `/api/v1/agents/centralized/${name}/graph/execution/${executionId}`
    )
    return response.data
  }

  /**
   * List agent versions
   */
  async getAgentVersions(name: string): Promise<{
    agent_name: string
    versions: Array<{
      version: string
      status: string
      is_latest: boolean
      created_at: string
      changelog?: string
    }>
  }> {
    const response = await this.client.get(`/api/v1/agents/centralized/${name}/versions`)
    return response.data
  }

  /**
   * Rollback agent to a specific version
   */
  async rollbackAgent(name: string, targetVersion: string, reason: string): Promise<{
    success: boolean
    agent_name: string
    previous_version: string
    new_version: string
    message: string
  }> {
    const response = await this.client.post(`/api/v1/agents/centralized/${name}/rollback`, null, {
      params: { target_version: targetVersion, reason }
    })
    return response.data
  }

  // ==================== Generic HTTP Methods ====================
  // These are provided for flexibility when specific methods don't exist

  /**
   * Generic GET request
   */
  async get<T = any>(url: string, config?: { params?: Record<string, any> }): Promise<{ data: T }> {
    return this.client.get<T>(url, config)
  }

  /**
   * Generic POST request
   */
  async post<T = any>(url: string, data?: any, config?: { params?: Record<string, any> }): Promise<{ data: T }> {
    return this.client.post<T>(url, data, config)
  }

  /**
   * Generic PUT request
   */
  async put<T = any>(url: string, data?: any, config?: { params?: Record<string, any> }): Promise<{ data: T }> {
    return this.client.put<T>(url, data, config)
  }

  /**
   * Generic PATCH request
   */
  async patch<T = any>(url: string, data?: any, config?: { params?: Record<string, any> }): Promise<{ data: T }> {
    return this.client.patch<T>(url, data, config)
  }

  /**
   * Generic DELETE request
   */
  async delete(url: string, config?: { params?: Record<string, any> }): Promise<void> {
    await this.client.delete(url, config)
  }

  // ==================== Intelligent Execution Endpoints ====================

  /**
   * Analyze a task and return execution plan without executing
   */
  async analyzeIntelligent(prompt: string): Promise<{
    original_prompt: string
    tasks: Array<{
      id: string
      description: string
      specialist_type: string
      complexity: number
      dependencies: string[]
    }>
    parallel_groups: string[][]
    total_complexity: number
    estimated_cost: number
  }> {
    const response = await this.client.post('/api/v1/intelligent/analyze', { prompt })
    return response.data
  }

  /**
   * Execute a task with AI-driven orchestration
   */
  async executeIntelligent(params: {
    prompt: string
    quality_threshold?: number
    max_revisions?: number
    workspace_id?: string
  }): Promise<{
    execution_id: string
    status: string
    final_output: string
    task_results: Record<string, {
      specialist: string
      quality_score: number | null
      revision_count: number
      result_preview: string
    }>
    plan: {
      tasks: number
      parallel_groups: string[][]
      complexity: number
    }
    quality_score: number
    total_cost: number
  }> {
    const response = await this.client.post('/api/v1/intelligent/execute', params)
    return response.data
  }

  /**
   * Quick execute a simple task with defaults
   */
  async quickExecuteIntelligent(prompt: string): Promise<{
    execution_id: string
    output: string
    quality_score: number
  }> {
    const response = await this.client.post('/api/v1/intelligent/quick-execute', { prompt })
    return response.data
  }

  /**
   * Get available LLM provider status
   */
  async getIntelligentProviders(): Promise<{
    local: boolean
    openai: boolean
    anthropic: boolean
    google: boolean
  }> {
    const response = await this.client.get('/api/v1/intelligent/providers')
    return response.data
  }

  /**
   * Get available specialist types
   */
  async getIntelligentSpecialists(): Promise<Array<{
    type: string
    description: string
  }>> {
    const response = await this.client.get('/api/v1/intelligent/specialists')
    return response.data
  }

  /**
   * Get intelligent execution configuration
   */
  async getIntelligentConfig(): Promise<{
    enabled: boolean
    ai_task_breakdown: boolean
    ai_quality_review: boolean
    parallel_detection: boolean
    default_quality_threshold: number
    max_revision_iterations: number
    prefer_local_llm: boolean
    auto_fallback_to_cloud: boolean
  }> {
    const response = await this.client.get('/api/v1/intelligent/config')
    return response.data
  }

  // ===========================================
  // Ziro Graph Visualization Methods
  // ===========================================

  /**
   * Get Ziro Intelligence graph for visualization
   */
  async getZiroGraph(): Promise<{
    nodes: Array<{
      id: string
      name: string
      type: string
      description: string
      position: { x: number; y: number }
      icon: string
      specialist_type?: string
      status: string
    }>
    edges: Array<{
      source: string
      target: string
      label: string
      condition: string | null
      is_conditional: boolean
    }>
    node_groups: Record<string, string[]>
    layout: {
      direction: string
      layers: string[][]
    }
  }> {
    const response = await this.client.get('/api/v1/intelligent/graph')
    return response.data
  }

  /**
   * Get Ziro graph in workflow builder format
   */
  async getZiroGraphAsWorkflow(): Promise<{
    id: string
    name: string
    description: string
    is_system: boolean
    nodes: Array<{
      id: string
      type: string
      position: { x: number; y: number }
      data: Record<string, any>
    }>
    edges: Array<{
      id: string
      source: string
      target: string
      label: string
      animated: boolean
      data: Record<string, any>
    }>
    metadata: Record<string, any>
  }> {
    const response = await this.client.get('/api/v1/intelligent/graph/workflow')
    return response.data
  }

  /**
   * Get full Ziro graph structure with metadata
   */
  async getZiroGraphStructure(): Promise<{
    nodes: Array<{
      id: string
      name: string
      type: string
      description: string
      position: { x: number; y: number }
      metadata: Record<string, any>
    }>
    edges: Array<{
      source: string
      target: string
      label: string
      condition: string | null
      is_conditional: boolean
    }>
    entry_point: string
    metadata: Record<string, any>
  }> {
    const response = await this.client.get('/api/v1/intelligent/graph/structure')
    return response.data
  }

  // ===========================================
  // Unified Execution Methods
  // ===========================================

  /**
   * Get unified agents list
   */
  async getUnifiedAgents(params?: {
    is_ziro_specialist?: boolean
    is_template?: boolean
    is_custom?: boolean
    provider_mode?: string
    specialist_type?: string
  }): Promise<{
    agents: Array<{
      id: string
      name: string
      description: string
      specialist_type?: string
      is_ziro_specialist: boolean
      is_template: boolean
      is_custom: boolean
      provider_mode: string
      execution_mode: string
      local_model?: string
      api_provider?: string
      quality_threshold: number
      max_revisions: number
      enable_quality_review: boolean
      complexity_threshold: number
    }>
  }> {
    const response = await this.client.get('/api/v1/unified/agents', { params })
    return response.data
  }

  /**
   * Get unified provider status
   */
  async getUnifiedProviders(): Promise<{
    local: boolean
    openai: boolean
    anthropic: boolean
    google: boolean
    default_provider: string
    ollama_models?: string[]
  }> {
    const response = await this.client.get('/api/v1/unified/providers')
    return response.data
  }

  /**
   * Get Ziro specialists list
   */
  async getUnifiedSpecialists(): Promise<Array<{
    id: string
    name: string
    specialist_type: string
    description: string
    provider_mode: string
    execution_mode: string
  }>> {
    const response = await this.client.get('/api/v1/unified/specialists')
    return response.data
  }

  /**
   * Execute unified agent request
   */
  async executeUnified(request: {
    prompt: string
    agent_id?: string
    agent_ids?: string[]
    options?: {
      provider_mode?: string
      execution_mode?: string
      quality_threshold?: number
      max_revisions?: number
      temperature?: number
      use_ai_orchestration?: boolean
    }
  }): Promise<{
    execution_id: string
    status: string
    output: string
    quality_score?: number
    revision_count: number
    provider_used: string
    tokens_used?: number
    task_results?: Array<{
      specialist_type: string
      status: string
      quality_score?: number
      revision_count: number
      output?: string
    }>
    metadata?: Record<string, any>
  }> {
    const response = await this.client.post('/api/v1/unified/execute', request)
    return response.data
  }

  /**
   * Analyze prompt for unified execution
   */
  async analyzeUnified(prompt: string): Promise<{
    complexity: number
    recommended_provider: string
    recommended_execution_mode: string
    detected_specialist_types: string[]
    estimated_tokens: number
    suggestions: string[]
  }> {
    const response = await this.client.post('/api/v1/unified/analyze', { prompt })
    return response.data
  }

  // =========================================================================
  // System Status & Database Health
  // =========================================================================

  /**
   * Get comprehensive system status including all databases
   */
  async getSystemStatus(): Promise<{
    status: string
    timestamp: string
    databases: Record<string, {
      status: string
      latency_ms?: number
      host?: string
      error?: string
      details?: any
      classes?: number
      class_names?: string[]
      collections?: number
      collection_names?: string[]
    }>
    summary: {
      healthy: number
      total: number
    }
  }> {
    const response = await this.client.get('/api/v1/system/status')
    return response.data
  }

  /**
   * Get status for a specific database
   */
  async getDatabaseStatus(database: string): Promise<{
    database: string
    timestamp: string
    status: string
    latency_ms?: number
    host?: string
    error?: string
  }> {
    const response = await this.client.get(`/api/v1/system/status/${database}`)
    return response.data
  }

  /**
   * Get AMLKG (Neo4j) summary with layer statistics
   */
  async getAMLKGSummary(): Promise<{
    status: string
    timestamp: string
    layers: {
      l1_episodic: { node_count?: number; avg_confidence?: number }
      l2_semantic: { node_count?: number; avg_confidence?: number }
      l3_procedural: { node_count?: number; avg_confidence?: number }
      l4_conceptual: { node_count?: number; avg_confidence?: number }
      l5_meta: { node_count?: number; avg_confidence?: number }
    }
    total_nodes: number
    episode_type_distribution: Record<string, number>
    error?: string
  }> {
    const response = await this.client.get('/api/v1/system/amlkg/summary')
    return response.data
  }

  /**
   * Get Memgraph summary
   */
  async getMemgraphSummary(): Promise<{
    status: string
    timestamp: string
    node_count: number
    edge_count: number
    labels: string[]
    error?: string
  }> {
    const response = await this.client.get('/api/v1/system/memgraph/summary')
    return response.data
  }

  // ==================== NeuralGraph (Phase 1 proxy endpoints) ====================

  async getNeuralGraphHealth(): Promise<{
    reachable: boolean
    latency_ms?: number
    ues_enabled: boolean
    api_key_configured: boolean
    default_timeout_s: number
    last_extraction_at?: string
    drift_status?: string
    drift_reasons?: string[]
    drift_episodes?: { in_memgraph: number; in_weaviate: number; missing_layer: number; missing_in_weaviate: number }
    drift_entities?: { in_memgraph: number; with_layer: number; missing_layer: number; sales_playbook_tagged?: number }
    error?: string
  }> {
    const response = await this.client.get('/api/v1/neuralgraph/health')
    return response.data
  }

  async getNeuralGraphSummary(): Promise<{
    status: string
    timestamp?: string
    layers: Record<string, { count: number; edge_count?: number; ttl_days?: number }>
    total_nodes: number
    episode_type_distribution?: Record<string, number>
  }> {
    const response = await this.client.get('/api/v1/neuralgraph/summary')
    return response.data
  }

  async getNeuralGraphEpisodes(params?: {
    agent_name?: string
    tags?: string[]
    project_id?: string
    since?: string
    limit?: number
    cursor?: string
  }): Promise<{
    episodes: Array<{
      id: string
      timestamp?: string
      episode_type?: string
      agent_name?: string
      tags?: string[]
      tenant_id?: string
      project_id?: string
      quality_score?: number
      confidence?: number
      content?: any
      summary?: string
      strength?: number
      layer?: string
    }>
    next_cursor?: string
    total_returned: number
  }> {
    const response = await this.client.get('/api/v1/neuralgraph/episodes', { params })
    return response.data
  }

  async getNeuralGraphEntities(params?: {
    query?: string
    limit?: number
    project_id?: string
    domain?: string
  }): Promise<{
    entities: Array<{
      memory_id: string
      name?: string
      entity_type?: string
      confidence?: number
      description?: string
      score?: number
      layer?: string
      domain?: string
    }>
    total_returned: number
  }> {
    const response = await this.client.get('/api/v1/neuralgraph/entities', { params })
    return response.data
  }

  async neuralGraphExtract(payload: {
    intent: 'inspect' | 'evolve' | 'create' | 'audit'
    query: string
    context?: Record<string, any>
    timeout_s?: number
    project_id?: string
  }): Promise<{
    success: boolean
    extraction_id?: string
    intent: string
    subject?: any
    graph?: { nodes: any[]; edges: any[]; paths: any[] }
    context_document: string
    confidence: number
    metadata?: Record<string, any>
  }> {
    const response = await this.client.post('/api/v1/neuralgraph/extract', payload)
    return response.data
  }

  async neuralGraphReinforce(edge_ids: string[], strength = 1.0, reason?: string): Promise<{
    ok: boolean
    strengthened_count: number
  }> {
    const response = await this.client.post('/api/v1/neuralgraph/reinforce', { edge_ids, strength, reason })
    return response.data
  }

  async getNeuralGraphWrites(params?: {
    agent_name?: string
    since?: string
    limit?: number
  }): Promise<{
    items: Array<{
      timestamp?: string
      agent_name: string
      request_id: string
      layers_written: string[]
      verification_verdict?: string
      duration_ms?: number
      total_tokens?: number
      total_cost_usd?: number
      success: boolean
      ues_extraction_id?: string
      orchestration_id?: string
    }>
    total: number
  }> {
    const response = await this.client.get('/api/v1/neuralgraph/writes', { params })
    return response.data
  }

  // ==================== PII Proxy (simulation + metrics — Phase 1/2/3) ===

  async previewPII(payload: {
    text: string
    project_id?: string
    mutation_preset?: 'none' | 'case_flip' | 'add_spacing' | 'broken_guillemets' | 'custom'
    simulate_llm_output?: string
    persist_session?: boolean
  }): Promise<{
    original_text: string
    scan_matches: Array<{
      category: string
      start: number
      end: number
      confidence: number
      tier: number
      pattern_name: string
      value_masked: string
    }>
    tokenised_text: string
    tokens_created: Array<{ token: string; category: string; masked_value: string }>
    restored_text: string
    tokens_resolved: number
    tokens_unresolved: number
    mutations_repaired: number
    scan_duration_ms: number
    tokenise_duration_ms: number
    detokenise_duration_ms: number
    token_inflation_pct: number
    pattern_version: string
    session_id: string
    session_ttl_seconds?: number
  }> {
    const response = await this.client.post('/api/v1/pii-proxy/preview', payload)
    return response.data
  }

  async getPIIMetricsSummary(): Promise<{
    scan: Record<string, any>
    tokenisation: Record<string, any>
    sessions: Record<string, any>
    category_distribution_24h: Record<string, number>
    canary: Record<string, any>
    ner: Record<string, any>
    chaos: Record<string, any>
  }> {
    const response = await this.client.get('/api/v1/pii-proxy/metrics/summary')
    return response.data
  }

  async getPIIDensity(window_hours = 24): Promise<{
    window_hours: number
    bin_minutes: number
    bins: Array<{ ts: string; detections: number; by_category: Record<string, number> }>
    total_detections: number
  }> {
    const response = await this.client.get('/api/v1/pii-proxy/metrics/density', { params: { window_hours } })
    return response.data
  }

  async runPIIEdgeCases(): Promise<{
    total: number
    passed: number
    failed: number
    duration_ms: number
    failures: Array<{ id: string; input: string; expected: string; actual: string; tags: string[] }>
    by_tag: Record<string, { passed: number; failed: number }>
  }> {
    const response = await this.client.post('/api/v1/pii-proxy/edge-cases/run')
    return response.data
  }

  async getUESActivity(window_hours = 24): Promise<{
    bins: Array<{ ts: string; extractions: number; avg_confidence: number; degraded_pct: number }>
    total_extractions: number
    avg_confidence: number
    degraded_pct: number
  }> {
    const response = await this.client.get('/api/v1/audit/ues-activity', { params: { window_hours } })
    return response.data
  }

  /**
   * Get Weaviate summary
   */
  async getWeaviateSummary(): Promise<{
    status: string
    timestamp: string
    class_count: number
    classes: Array<{ name: string; properties: number }>
    error?: string
  }> {
    const response = await this.client.get('/api/v1/system/weaviate/summary')
    return response.data
  }

  /**
   * Get AMLKG health status
   */
  async getAMLKGHealth(): Promise<{
    status: string
    neo4j_connected: boolean
    neo4j_uri: string
    layer_status: Record<string, string>
    message?: string
  }> {
    const response = await this.client.get('/api/v1/amlkg/health')
    return response.data
  }

  /**
   * Get AMLKG episodes (L1)
   */
  async getAMLKGEpisodes(params?: {
    limit?: number
    offset?: number
    episode_type?: string
  }): Promise<{
    items: Array<{
      id: string
      timestamp: string
      episode_type: string
      content: string
      quality_score: number
      confidence: number
      context: Record<string, any>
    }>
    total: number
    limit: number
    offset: number
  }> {
    const response = await this.client.get('/api/v1/amlkg/episodes', { params })
    return response.data
  }

  /**
   * Trigger AMLKG consolidation
   */
  async triggerAMLKGConsolidation(): Promise<{
    source_layer: string
    target_layer: string
    episodes_processed: number
    entities_created: number
  }> {
    const response = await this.client.post('/api/v1/amlkg/consolidate', {})
    return response.data
  }

  // ==================== Documentation API ====================

  /**
   * Get documentation index with all available files by category
   */
  async getDocsIndex(): Promise<{
    categories: Record<string, Array<{
      name: string
      path: string
      category: string
      title: string
    }>>
    total_files: number
  }> {
    const response = await this.client.get('/api/v1/docs/')
    return response.data
  }

  /**
   * Get the content of a specific documentation file
   */
  async getDocContent(path: string): Promise<{
    name: string
    path: string
    category: string
    title: string
    content: string
  }> {
    const response = await this.client.get(`/api/v1/docs/content/${path}`)
    return response.data
  }

  /**
   * Get the download URL for a documentation file
   */
  getDocDownloadUrl(path: string): string {
    return `${API_BASE_URL}/api/v1/docs/download/${path}`
  }

  /**
   * Get the URL to download all documentation as a ZIP file
   */
  getDocsDownloadAllUrl(): string {
    return `${API_BASE_URL}/api/v1/docs/download-all`
  }
}

export const apiClient = new APIClient()

// Expose raw axios methods for direct API calls (used by some pages like ArgusObserverPage)
export const rawApi = {
  get: <T = any>(url: string, config?: any) => apiClient['client'].get<T>(url, config),
  post: <T = any>(url: string, data?: any, config?: any) => apiClient['client'].post<T>(url, data, config),
  put: <T = any>(url: string, data?: any, config?: any) => apiClient['client'].put<T>(url, data, config),
  delete: <T = any>(url: string, config?: any) => apiClient['client'].delete<T>(url, config),
}

export default apiClient
